import { Component, ComponentFactoryResolver, Injector, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Action, ConfirmDialogConfig, CrudConfirmDialogComponent, CrudEditComponent, CrudEditorParentService, CrudEditorSelfService, FormStorage, openCrudRowsEditorDialogInAttributeEditorMode, REPOSITORY_FILE_ROWS_TYPE, UserStorage } from '@scisys-uim/uim-common';
import { ASSETS_USER_DETAILS_TYPE, Firearm, FIREARM_TYPE, FirearmAssignment, RANGER_TYPE } from 'assets-model';
import { FirearmAssignmentService, FirearmDocumentPermission, FirearmDocumentService } from 'assets-service';
import { openJustfoneDataTransferDialog } from '../../datatransfer/dialog/justfone-data-transfer-dialog.component';
import { FirearmDocumentEditDialogComponent, FirearmDocumentEditDialogConfig, FirearmDocumentRowsComponent, FirearmDocumentRowsComponentMode } from '../../firearm/editor/firearm-document-rows.component';


@Component({
  selector: 'app-firearm-assignment-editor',
  templateUrl: '../../../../../../../node_modules/@scisys-uim/uim-common/src/lib/crud-components/crud-edit.component.html',
  styleUrls: ['../../../../../../../node_modules/@scisys-uim/uim-common/src/lib/crud-components/crud-edit.component.css'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class FirearmAssignmentEditorComponent extends CrudEditComponent<FirearmAssignment, number> implements OnInit {

  constructor(
    private firearmAssignmentService: FirearmAssignmentService,
    private firearmDocumentService: FirearmDocumentService,
    route: ActivatedRoute,
    router: Router,
    userStorage: UserStorage,
    dialog: MatDialog,
    snackbar: MatSnackBar,
    componentFactoryResolver: ComponentFactoryResolver,
    injector: Injector,
    private formStorage: FormStorage) {
    super(route, router, userStorage, dialog, snackbar, componentFactoryResolver, injector);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  itemSet() {
    super.itemSet();
    var assigneeName = this.item?.firearmAssignmentType == 'ISSUE' ? this.item?.ranger?.name : this.item?.user?.user;
    var prefix = (this.readOnly ? "View " : (this.createMode ? "Create " : "Edit ")) + this.itemTypeTitle;
    this.editorTitle = prefix + ' - ' + assigneeName;


    const self = this;
    this.actions = [];

    this.actions.push(<Action>{
      title: 'Resend Police Notification',
      get enabled(): boolean {
        return true;
      },
      color: true,
      get available(): boolean {
        return !self.createMode && self.userStorage.currentUserHasAnyAuthority(['NOTIFY_POLICE_FIREARM_ASSIGNMENT']) && self.item?.policeNotificationEligible;
      },
      doAction(...args) {
        self.openResendPoliceNotificationPopup();
      }
    });

    this.actions.push(<Action>{
      title: 'View Evidence',
      get enabled(): boolean {
        return true;
      },
      color: true,
      get available(): boolean {
        return !self.createMode && self.item?.storageType == 'DISPOSE';
      },
      doAction(...args) {
        self.openFirearmDisposalDocumentDialog();
      }
    });

    this.actions.push(<Action>{
      title: 'Firearm',
      get enabled(): boolean {
        return self.item?.firearm != null;
      },
      color: true,
      get available(): boolean {
        return !self.createMode && self.userStorage.currentUserHasAnyAuthority(['READ_FIREARM']);
      },
      getRoute(...args) {
        return [...FIREARM_TYPE.viewRouteBase, self.item?.firearm?.assetId];
      }
    });

    this.actions.push(<Action>{
      title: 'Assignee Ranger',
      get enabled(): boolean {
        return self.item?.ranger != null;
      },
      color: true,
      get available(): boolean {
        return !self.createMode && self.userStorage.currentUserHasAnyAuthority(['READ_RANGER']);
      },
      getRoute(...args) {
        return [...RANGER_TYPE.viewRouteBase, self.item?.ranger?.rangerId];
      }
    });

    this.actions.push(<Action>{
      title: 'Assignee User',
      get enabled(): boolean {
        return self.item?.user != null;
      },
      color: true,
      get available(): boolean {
        return !self.createMode && self.userStorage.currentUserHasAnyAuthority(['READ_ASSETS_USER_DETAILS']);
      },
      getRoute(...args) {
        return [...ASSETS_USER_DETAILS_TYPE.viewRouteBase, self.item?.user?.user];
      }
    });

    this.actions.push(<Action>{
      title: 'Justfone Debug',
      get enabled(): boolean { return !self.unsavedChanges() },
      color: "primary",
      get available(): boolean {
        return self.currentUserHasAnyAuthority("IS_SUPERUSER");
      },
      doAction() {
        openJustfoneDataTransferDialog(self.dialog, 'FirearmAssignment', self.item.firearmAssignmentId);
      }
    });
  }

  allowsEdit(item: FirearmAssignment): boolean {
    return false;
  }


  openResendPoliceNotificationPopup() {
    const self = this;
    var dialogRef: MatDialogRef<CrudConfirmDialogComponent, boolean> = this.openConfirmationDialog(this.item, {
      data: <ConfirmDialogConfig<FirearmAssignment, number>>{
        title: 'Resend Police Notification?',
        action: 'Confirm',
      }
    });
    dialogRef.afterClosed().subscribe((confirm: boolean) => {
      if (confirm) {
        this.firearmAssignmentService.resendPoliceNotification(self.item.firearmAssignmentId).subscribe(result => {
          this.openSnackbar('Police Notification Sent');
          this.service.get(result.firearmAssignmentId).subscribe(firearm => {
            this.postSave(firearm);
            this.itemComponent.setDefaultDisabledFields();
          });
        });
      }
    });
  }

  openFirearmDisposalDocumentDialog() {
    this.firearmDocumentService.getFirearmPermissions(this.item.firearm.assetId, null).subscribe(permissions => {
      const firearmDocumentsPermissions = <FirearmDocumentPermission[]>permissions;
      const showActions = [];
      const modesAvailable: FirearmDocumentRowsComponentMode[] = [];
      if (this.userDetails.hasAnyAuthority('READ_FIREARM_DOCUMENT')) {
        showActions.push('download');
        modesAvailable.push('LIST_DOCUMENTS');
      }
      if (this.userDetails.hasAnyAuthority('UPLOAD_FIREARM_DOCUMENT')) {
        showActions.push('delete');
        modesAvailable.push('UPLOAD_DOCUMENTS');
      }
      const dialogConfig: FirearmDocumentEditDialogConfig<number> = {
        dialogTitle: 'Firearm Disposal Documents',
        showActions: showActions,
        modesAvailable: modesAvailable,
        permissions: firearmDocumentsPermissions,
        firearm: this.item.firearm,
        uploadDocType: 'DISPOSAL',
        allowSelectUploadDocType: false,
        config: {
          rowsItemComponentType: FirearmDocumentRowsComponent,
          rowsType: REPOSITORY_FILE_ROWS_TYPE,
          readOnly: true
        },
        supportsAdd: false,
        supportsSave: false,
        rows: [],
      };

      openCrudRowsEditorDialogInAttributeEditorMode(this.dialog, {
        panelClass: ['contract-document-dialog', 'dialog-resizable'],
        height: '80vh',
        minWidth: '60vw',
        data: dialogConfig
      }, FirearmDocumentEditDialogComponent);
    });
  }
}






